import axios, { AxiosRequestConfig } from "axios";
import { formatLocalDateTime } from "@/helpers/timeFormatter";
import { isPlainObject } from "@/helpers/shared";

export const customAxios = axios.create();

const serializePostBody = (body: unknown): unknown => {
  if (body instanceof Date) {
    return formatLocalDateTime(body);
  }

  if (Array.isArray(body)) {
    return body.map((item) => serializePostBody(item));
  }

  if (isPlainObject(body)) {
    return Object.entries(body).reduce<Record<string, unknown>>((acc, [key, value]) => {
      acc[key] = serializePostBody(value);
      return acc;
    }, {});
  }

  return body;
};

// Interceptors will be configured in the plugin
export const httpRequestServer = {
  get: async (url: string, configOptions?: AxiosRequestConfig): Promise<any> => {
    const response = await customAxios.get(url, configOptions);
    return response?.data;
  },
  post: async (url: string, body?: unknown, configOptions?: AxiosRequestConfig): Promise<any> => {
    const serializedBody = serializePostBody(body);
    const response = await customAxios.post(url, serializedBody, configOptions);
    return response?.data;
  },
  delete: async (url: string, configOptions?: AxiosRequestConfig): Promise<any> => {
    const response = await customAxios.delete(url, configOptions);
    return response?.data;
  },
  // For a file download: the body as a Blob, plus the response headers, which carry the
  // server's file name (Content-Disposition).
  postForFile: async (
    url: string,
    body?: unknown,
    configOptions?: AxiosRequestConfig,
  ): Promise<{ blob: Blob; headers: Record<string, unknown> }> => {
    const serializedBody = serializePostBody(body);
    const response = await customAxios.post<Blob>(url, serializedBody, {
      ...configOptions,
      responseType: "blob",
    });
    return { blob: response?.data, headers: (response?.headers ?? {}) as Record<string, unknown> };
  },
  getForFile: async (
    url: string,
    configOptions?: AxiosRequestConfig,
  ): Promise<{ blob: Blob; headers: Record<string, unknown> }> => {
    const response = await customAxios.get<Blob>(url, { ...configOptions, responseType: "blob" });
    return { blob: response?.data, headers: (response?.headers ?? {}) as Record<string, unknown> };
  },
};
